import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ImageField from "@/components/admin/ImageField";

/**
 * Full CRUD editor for the `services` collection.
 * Fields: slug (auto if empty), name, tagline, image, features[], enabled, order.
 * Reorder via up/down. Delete with confirm. Save is per-row.
 *
 * NOTE: Slug drives the public URL /services/{slug} — changing it AFTER links exist will 404 the old URL.
 * We show a warning next to the slug field.
 */
export default function ServicesAdmin() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/services"); setRows(r.data); }
    catch { toast.error("Could not load services"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (i, k, v) => setRows(prev => prev.map((r, j) => j === i ? { ...r, [k]: v } : r));

  const save = async (row) => {
    if (!row.name?.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: row.name, slug: row.slug || "", tagline: row.tagline || "",
        image: row.image || "", features: row.features || [],
        enabled: row.enabled !== false, order: row.order ?? 0,
      };
      if (row.id) await api.put(`/admin/services/${row.id}`, payload);
      else await api.post(`/admin/services`, payload);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete service "${row.name}"?  The URL /services/${row.slug} will 404.`)) return;
    try { await api.delete(`/admin/services/${row.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const reordered = [...rows]; [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    setRows(reordered);
    try { await api.post(`/admin/services/reorder`, { ordered_ids: reordered.map(r => r.id).filter(Boolean) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const add = () => setRows(prev => [...prev, {
    name: "", slug: "", tagline: "", image: "", features: [],
    enabled: true, order: prev.length,
  }]);

  return (
    <div className="space-y-4" data-testid="services-list">
      {rows.map((row, i) => (
        <div key={row.id || `new-${i}`}
             className={`bg-[#0a0a0a] border border-white/10 p-5 ${row.enabled === false ? "opacity-60" : ""}`}
             data-testid={`services-row-${i}`}>
          <div className="flex flex-col md:flex-row gap-5">
            {/* Image on the left */}
            <div className="w-full md:w-52 shrink-0">
              <ImageField
                label="Service image"
                value={row.image || ""}
                onChange={(url) => patch(i, "image", url)}
                aspect="aspect-[4/3]"
                variant="large"
                testId={`services-row-${i}-image`}
              />
            </div>

            {/* Reorder + enabled */}
            <div className="flex md:flex-col items-center gap-2 pt-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                className="p-1.5 border border-white/15 hover:border-[#D4AF37] disabled:opacity-30" data-testid={`services-row-${i}-up`}>
                <ArrowUp size={12} />
              </button>
              <button onClick={() => patch(i, "enabled", !(row.enabled !== false))} title={row.enabled !== false ? "Disable" : "Enable"}
                className={`p-1.5 border ${row.enabled !== false ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-white/50"}`}
                data-testid={`services-row-${i}-toggle`}>
                {row.enabled !== false ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="Move down"
                className="p-1.5 border border-white/15 hover:border-[#D4AF37] disabled:opacity-30" data-testid={`services-row-${i}-down`}>
                <ArrowDown size={12} />
              </button>
            </div>

            {/* Fields */}
            <div className="flex-1 grid gap-3">
              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Name</span>
                <input value={row.name || ""} onChange={e => patch(i, "name", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                  data-testid={`services-row-${i}-name`} />
              </label>

              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">
                  Slug (URL) <span className="text-white/30 normal-case">— leave empty to auto-generate; changing this breaks old links</span>
                </span>
                <input value={row.slug || ""} onChange={e => patch(i, "slug", e.target.value)}
                  placeholder="wedding-photography"
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none font-mono"
                  data-testid={`services-row-${i}-slug`} />
              </label>

              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Tagline (short description)</span>
                <textarea rows={2} value={row.tagline || ""} onChange={e => patch(i, "tagline", e.target.value)}
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                  data-testid={`services-row-${i}-tagline`} />
              </label>

              <label className="block">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">
                  "What's Included" bullets <span className="text-white/30 normal-case">— one per line</span>
                </span>
                <textarea rows={5} value={(row.features || []).join("\n")}
                  onChange={e => patch(i, "features", e.target.value.split("\n"))}
                  placeholder="A calm, story-first approach&#10;Two photographers covering different angles"
                  className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
                  data-testid={`services-row-${i}-features`} />
              </label>

              <div className="flex gap-2 pt-1">
                <button onClick={() => save(row)} disabled={busy} className="btn-gold text-xs" data-testid={`services-row-${i}-save`}>
                  <Save size={12} /> Save
                </button>
                {row.id && (
                  <button onClick={() => remove(row)} className="btn-ghost text-xs text-red-300 hover:text-red-500 border-red-500/40"
                    data-testid={`services-row-${i}-delete`}>
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      <button onClick={add} className="btn-ghost w-full justify-center py-4 border-dashed" data-testid="services-add">
        <Plus size={14} /> Add service
      </button>
    </div>
  );
}
